/**
 * FileUpload — Drag-and-drop file upload component.
 */

import { useState, useRef, useCallback } from 'react';

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  acceptedTypes?: string[];
  maxSizeMB?: number;
  disabled?: boolean;
}

export function FileUpload({
  onFileSelect,
  acceptedTypes = ['.pdf', '.txt', '.md', '.csv', '.json', '.stix', '.ioc', '.yar', '.yara', '.yml', '.yaml', '.log', '.syslog', '.evtx'],
  maxSizeMB = 100,
  disabled = false,
}: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback(
    (file: File): string | null => {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!acceptedTypes.includes(ext)) {
        return `Unsupported file type: ${ext}. Accepted: ${acceptedTypes.join(', ')}`;
      }
      if (file.size > maxSizeMB * 1024 * 1024) {
        return `File too large. Maximum: ${maxSizeMB} MB`;
      }
      return null;
    },
    [acceptedTypes, maxSizeMB]
  );

  const handleFile = useCallback(
    (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        setSelectedFile(null);
        return;
      }
      setError(null);
      setSelectedFile(file);
      onFileSelect(file);
    },
    [validateFile, onFileSelect]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (disabled) return;
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [disabled, handleFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  return (
    <div className="w-full">
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`glass-card flex flex-col items-center justify-center min-h-[200px] cursor-pointer transition-all ${
          isDragging ? '!border-primary-green !bg-green-50/50' : ''
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary-blue'}`}
        role="button"
        tabIndex={0}
        aria-label="Upload CTI file. Click or drag and drop."
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        <svg className="w-12 h-12 text-primary-teal mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
        <p className="text-body font-semibold text-slate-text mb-2">
          {selectedFile ? selectedFile.name : 'Drop your CTI file here'}
        </p>
        <p className="text-[16px] text-slate-muted">
          {selectedFile
            ? `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`
            : `or click to browse · Max ${maxSizeMB} MB`}
        </p>
        <p className="text-[14px] text-slate-muted mt-2">
          Supported: PDF, TXT, MD, CSV, JSON, STIX, IOC, YARA, YML, LOG, EVTX
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={acceptedTypes.join(',')}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled}
      />

      {error && (
        <p className="mt-3 text-body font-medium text-red-700" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
